import type { ReactNode } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { AppIconButton } from '../../design-system';

export function EfemSetupList({ children }: { children: ReactNode }) {
  return (
    <Stack
      spacing={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {children}
    </Stack>
  );
}

interface EfemSetupListRowProps {
  primary: ReactNode;
  secondary?: ReactNode;
  meta?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  onDelete?: () => void;
  deleteLabel?: string;
  isLast?: boolean;
}

export function EfemSetupListRow({
  primary,
  secondary,
  meta,
  onEdit,
  editLabel = 'Edit',
  onDelete,
  deleteLabel = 'Remove',
  isLast = false,
}: EfemSetupListRowProps) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      px={1.5}
      py={1.25}
      sx={{
        minHeight: 52,
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {primary}
        </Typography>
        {(secondary || meta) && (
          <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mt={0.25}>
            {secondary && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {secondary}
              </Typography>
            )}
            {secondary && meta && (
              <Typography variant="caption" color="text.disabled">
                ·
              </Typography>
            )}
            {meta && (
              <Typography variant="caption" color="text.secondary">
                {meta}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <Box display="flex" flexShrink={0} gap={0.25}>
        {onEdit && (
          <Tooltip title={editLabel}>
            <AppIconButton size="small" onClick={onEdit} aria-label={editLabel}>
              <Edit fontSize="small" />
            </AppIconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title={deleteLabel}>
            <AppIconButton size="small" onClick={onDelete} aria-label={deleteLabel} color="error">
              <Delete fontSize="small" />
            </AppIconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export function EfemSetupListEmpty({
  message,
  action,
}: {
  message: string;
  action?: ReactNode;
}) {
  return (
    <Box
      px={2}
      py={2.5}
      textAlign="center"
      sx={{
        border: 1,
        borderStyle: 'dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2" color="text.secondary" gutterBottom={!!action}>
        {message}
      </Typography>
      {action}
    </Box>
  );
}
